export const chartGeometry = (bars, width = 900, height = 260) => {
  if (!Array.isArray(bars) || bars.length < 2) return null;
  const dates = bars.map((bar) => Date.parse(`${bar.session}T00:00:00Z`));
  const prices = bars.map((bar) => Number(bar.close));
  if (dates.some((value) => !Number.isFinite(value))
    || prices.some((value) => !Number.isFinite(value) || value <= 0)
    || dates.some((value, index) => index > 0 && value <= dates[index - 1])) return null;
  const start = dates[0];
  const span = dates.at(-1) - start;
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  const spread = high - low || 1;
  const x = (value) => 28 + ((value - start) / span) * (width - 56);
  const y = (value) => height - 28 - ((value - low) / spread) * (height - 56);
  return {
    points: bars.map((bar, index) => `${x(dates[index]).toFixed(2)},${y(prices[index]).toFixed(2)}`).join(' '),
    markerX: (session) => {
      const date = Date.parse(`${session}T00:00:00Z`);
      return Number.isFinite(date) && date >= start && date <= dates.at(-1) ? x(date) : null;
    },
    low,
    high,
  };
};

export const analogueWindowGeometry = (bars, width = 900, height = 260) => {
  if (!Array.isArray(bars) || bars.length < 2 || bars.length > 127) return null;
  const observed = bars.filter((bar) => bar.observed === true);
  const prices = observed.map((bar) => Number(bar.close));
  if (observed.length < 2 || prices.some((value) => !Number.isFinite(value) || value <= 0)) return null;
  const anchor = bars.findIndex((bar) => bar.phase === 'ANCHOR');
  if (anchor < 0 || bars.some((bar, index) => index && bar.session <= bars[index - 1].session)) return null;
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  const spread = high - low || 1;
  const x = (index) => 28 + (index / (bars.length - 1)) * (width - 56);
  const y = (value) => height - 28 - ((value - low) / spread) * (height - 56);
  const segments = [];
  let current = [];
  bars.forEach((bar, index) => {
    if (!bar.observed) {
      if (current.length) segments.push(current.join(' '));
      current = [];
    } else {
      current.push(`${x(index).toFixed(2)},${y(Number(bar.close)).toFixed(2)}`);
    }
  });
  if (current.length) segments.push(current.join(' '));
  return { segments, anchorX: x(anchor), afterWidth: x(bars.length - 1) - x(anchor),
    missingX: bars.flatMap((bar, index) => bar.observed ? [] : [x(index)]), low, high };
};

export const historicalQualityCopy = (quality) => {
  switch (quality?.status) {
    case 'VERIFIED': return 'Pinngro verified this historical record against its frozen source and completed the corpus.';
    case 'HISTORICAL_DATA_QUALITY_LIMITED': return `${quality.explanation || 'Part of this record cannot be reliably validated.'} Pinngro has deliberately withheld the historical chart and analysis rather than guessing.`;
    case 'INCREMENTAL_DATA_LIMITED': return `${quality.explanation || 'The latest provider session is missing.'} This stock is verified only through ${quality.evidence_date || 'its last published session'}. Its chart and analysis are withheld until the missing data is verified.`;
    case 'BUILDING': return 'Pinngro is still preparing and verifying this historical record. A validated member of an unfinished corpus is not ready for customer analysis.';
    case 'PENDING': return 'A completed, verified historical publication is not available yet.';
    case 'IDENTITY_CONFLICT': return 'Current identity conflicts with the frozen historical listing. Pinngro has withheld historical analysis until that conflict is resolved.';
    case 'UNSUPPORTED': return 'This security is outside the current historical share corpus. Company context remains available.';
    default: return 'Historical quality is unavailable right now. Pinngro cannot safely show this analysis.';
  }
};
